// Vendors
import { useEffect, useRef } from 'react';
import { Route, Routes, useLocation, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

// Pages
import AuthPage from './pages/AuthPage';
import Authorize from './pages/Authorize/Authorize';
import SamlAuth from './pages/SamlAuth/SamlAuth';
import SamlError from './pages/SamlAuth/SamlError/SamlError';
import VerifyConsent from './pages/VerifyConsent/VerifyConsent';
import ClientNotFound from './pages/NoSession/ClientNotFound/ClientNotFound';

// Components
import GlobalModals from './components/Modals/GlobalModals/GlobalModals';

// Selectors
import { selectClientId } from './reducers/auth/authSelectors';

// Helpers
import { retrieveAppUrl, clearAuthSession } from './reducers/auth/authAuthorizeHelpers';

function App() {
  const location = useLocation();
  const dispatch = useDispatch();
  const clientId = useSelector(selectClientId);
  const { t, i18n } = useTranslation('auth');

  const storedAppUrl = retrieveAppUrl();
  const hasWipedRef = useRef(false);

  const hasAuthContext = Boolean(clientId);

  const isOAuthRoute =
    location.pathname.startsWith('/txtsso/auth/v1/authorize');

  const isSamlRoute =
    location.pathname.startsWith('/txtsso/saml/v1/auth');

  const isSamlErrorRoute = location.pathname === '/saml-error';

  const isEntryRoute = isOAuthRoute || isSamlRoute || isSamlErrorRoute;

  useEffect(() => {
    const userAgent = (window.navigator.userAgent || '').toLowerCase();
    const isIOS = /iphone|ipod|ipad/.test(userAgent);

    document.body.classList.toggle('device-ios', isIOS);

    return () => {
      document.body.classList.remove('device-ios');
    };
  }, []);

// Update document title
  useEffect(() => {
    document.documentElement.lang = i18n.language;
    document.title = t('documentTitle');
  }, [i18n.language, t]);

  // Track page views for Google Analytics on route changes
  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: location.pathname,
      });
    }
  }, [location.pathname]);

  // Session management: handle back button, reload, and bfcache
  useEffect(() => {
    if (isEntryRoute || hasWipedRef.current) return;

    const navigationType =
      performance.getEntriesByType?.('navigation')?.[0]?.type || 'navigate';

    if (navigationType === 'reload' && !hasAuthContext && storedAppUrl) {
      clearAuthSession();
      window.location.href = storedAppUrl;
      return;
    }

    const wipeSession = () => {
      hasWipedRef.current = true;
      clearAuthSession();
      dispatch({ type: 'RESET_ALL' });
    };

    if (navigationType === 'back_forward') {
      wipeSession();
    }

    const handlePageShow = (event) => {
      if (event.persisted) {
        wipeSession();
      }
    };

    window.addEventListener('pageshow', handlePageShow);
   return () => window.removeEventListener('pageshow', handlePageShow);
  }, [isEntryRoute, dispatch, hasAuthContext, storedAppUrl]);

  if (!isEntryRoute && !hasAuthContext) {
    const navigationType = performance.getEntriesByType?.('navigation')?.[0]?.type || 'navigate';    
    if (navigationType === 'reload' && storedAppUrl) {
      return <div className="sign-in-loading" role="status" aria-label="Loading" />;
    }
    return <ClientNotFound />;
  }

  return (
    <>
      <Routes>
        <Route
          path="/txtsso/auth/v1/authorize/*"
          element={<Authorize />}
        />

        <Route
          path="/txtsso/saml/v1/auth/*"
          element={<SamlAuth />}
        />

        <Route
          path="/saml-error"
          element={<SamlError />}
        />

        <Route
          path="/sign-in"
          element={<AuthPage />}
        />

        <Route
          path="/choose-mfa"
          element={<AuthPage />}
        />

        <Route
          path="/enter-otp"
          element={<AuthPage />}
        />

        <Route
          path="/continue-as"
          element={<VerifyConsent />}
        />

        <Route
          path="/verify-consent"
          element={<VerifyConsent />}
        />

        <Route
          path="*"
          element={<Navigate to="/sign-in" replace />}
        />
      </Routes>

      <GlobalModals />
    </>
  );
}

export default App;